import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import { unlikeSupporterComment } from '#src/app/pages/actions/shared/api';

type Result = ModelHookResult<
	EmptyObject,
	{
		unlike: (commentId: string) => Promise<unknown>;
	}
>;

export function useUnlike(): Result {
	const utilityContext = useUtilityContext();
	const [{ supporterVoices }, { setUnlikeError, unsetUnlikeError, updateSupporterVoice }] =
		usePetitionActionsPageState();
	const track = useTracking();

	// Prevent simultaneous calls to API. This is not a robust solution due to how the closure is scoped by the hook. We
	// should replace this with a useQuery implementation.
	let inFlight = false;

	const unlike = async (commentId: string) => {
		if (inFlight) {
			return;
		}

		try {
			// eslint-disable-next-line react-hooks/immutability
			inFlight = true;

			unsetUnlikeError(commentId);

			void track('petition_actions_supporter_voice_unlike');
			await unlikeSupporterComment(commentId, utilityContext);

			const prevSupporterVoice = supporterVoices.find((candidate) => candidate.id === commentId);
			if (!prevSupporterVoice) {
				return;
			}

			const supporterVoiceDraft = { ...prevSupporterVoice };
			supporterVoiceDraft.likeCount -= 1;
			supporterVoiceDraft.viewerLiked = false;
			updateSupporterVoice(supporterVoiceDraft);
		} catch {
			setUnlikeError(commentId);
		} finally {
			inFlight = false;
		}
	};

	return {
		data: {},
		actions: {
			unlike,
		},
	};
}
